import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, BarChart3, Check, Download, ExternalLink, Package, Shield, ShoppingCart, Tag } from 'lucide-react';
import { apiGet, buildApiUrl } from '../../lib/apiClient';
import { API_ROUTES } from '../../lib/apiRoutes';

export default function EAStoreDetail() {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [brokerConfirmed, setBrokerConfirmed] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');

  useEffect(() => {
    apiGet(API_ROUTES.public.eaStoreDetail(slug), { auth: false })
      .then((data) => {
        setItem(data.item);
        setSelectedImage(data.item?.gallery_images?.[0]?.src || data.item?.image_url || '');
      })
      .catch(() => setItem(null))
      .finally(() => setLoading(false));
  }, [slug]);

  const download = () => {
    if (!item) return;
    if (!item.is_free) {
      if (item.buy_url) window.open(item.buy_url, '_blank', 'noopener,noreferrer');
      else toast.error('Buy URL has not been configured yet.');
      return;
    }
    if (item.requires_broker_signup && !brokerConfirmed) {
      toast.error('Please confirm broker signup first.');
      return;
    }
    window.location.href = buildApiUrl(`${API_ROUTES.public.eaStoreDetail(slug)}/download${brokerConfirmed ? '?broker_confirmed=1' : ''}`);
  };

  if (loading) return <Shell><div className="py-24 text-center text-gray-500">Loading EA...</div></Shell>;
  if (!item) return <Shell><div className="py-24 text-center"><p className="text-gray-400">EA not found.</p><Link to="/ea-store" className="text-emerald-300 mt-3 inline-block">Back to store</Link></div></Shell>;

  const price = item.is_free ? 'Free Download' : `$${Number(item.price_usd || 0).toFixed(2)}`;
  const galleryImages = (Array.isArray(item.gallery_images) && item.gallery_images.length ? item.gallery_images : (item.image_url ? [{ name: item.name, src: item.image_url }] : [])).filter((image) => image && image.src);
  const activeImage = selectedImage || galleryImages[0]?.src || '';
  const features = Array.isArray(item.features) ? item.features : [];
  const tags = Array.isArray(item.tags) ? item.tags : [];

  return (
    <Shell>
      <Link to="/ea-store" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white mb-6"><ArrowLeft size={15} /> Back to EA Store</Link>
      <section className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-7">
        <div>
          <div className="rounded-xl border border-white/10 bg-white/[0.035] overflow-hidden mb-6">
            <div className="h-64 bg-[#101721] border-b border-white/10 flex items-center justify-center overflow-hidden">
              {activeImage ? <img src={activeImage} alt={item.name} className="w-full h-full object-cover" /> : <Package size={60} className="text-emerald-300" />}
            </div>
            <div className="p-6">
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <Badge icon={Package} text={item.platform} />
                {item.version && <Badge icon={Tag} text={`v${item.version}`} />}
                {item.tracking_url && <Badge icon={BarChart3} text="Live tracking" />}
              </div>
              <h1 className="text-3xl md:text-5xl font-bold text-white">{item.name}</h1>
              <p className="text-gray-400 text-lg mt-4">{item.short_description || 'Expert Advisor for automated trading.'}</p>
              {item.description && <p className="text-gray-300 leading-7 mt-6 whitespace-pre-line">{item.description}</p>}
            </div>
          </div>

          {!!galleryImages.length && (
            <div className="rounded-xl border border-white/10 bg-white/[0.035] p-6 mb-6">
              <h2 className="text-xl font-bold text-white mb-4">Result Gallery</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {galleryImages.map((image, index) => (
                  <button key={`${image.name || 'image'}-${index}`} type="button" onClick={() => setSelectedImage(image.src)} className={`rounded-lg overflow-hidden border bg-[#0d1117] ${activeImage === image.src ? 'border-emerald-400' : 'border-white/10 hover:border-white/30'}`}>
                    <img src={image.src} alt={image.name || `${item.name} result ${index + 1}`} className="w-full h-28 object-cover" />
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="rounded-xl border border-white/10 bg-white/[0.035] p-6">
            <h2 className="text-xl font-bold text-white mb-4">Features</h2>
            {features.length ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {features.map((feature) => <div key={feature} className="flex items-start gap-3 rounded-lg bg-[#0d1117] border border-white/10 p-3"><Check size={16} className="text-emerald-300 mt-0.5" /><span className="text-sm text-gray-300">{feature}</span></div>)}
              </div>
            ) : <p className="text-gray-500">No feature list has been added yet.</p>}
          </div>
        </div>

        <aside className="space-y-5">
          <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/5 p-5 sticky top-24">
            <p className="text-xs uppercase text-emerald-300 font-semibold">Access</p>
            <p className="text-3xl font-bold text-white mt-2">{price}</p>
            {item.requires_broker_signup && (
              <div className="rounded-lg border border-cyan-500/20 bg-cyan-500/5 p-4 mt-5">
                <div className="flex items-center gap-2 text-cyan-300 font-semibold text-sm"><Shield size={15} /> Broker signup required</div>
                <p className="text-xs text-gray-400 mt-2">This EA is free after signing up with the broker link below.</p>
                {item.broker_signup_url && <a href={item.broker_signup_url} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1 text-sm text-cyan-300 hover:text-cyan-200"><ExternalLink size={13} /> Open {item.broker_name || 'broker'} link</a>}
                <label className="flex items-start gap-2 mt-4 text-xs text-gray-300 cursor-pointer">
                  <input type="checkbox" checked={brokerConfirmed} onChange={(e) => setBrokerConfirmed(e.target.checked)} className="mt-0.5 accent-emerald-500" />
                  I signed up using the broker link and want to download this EA.
                </label>
              </div>
            )}
            <button onClick={download} className="mt-5 w-full flex items-center justify-center gap-2 rounded-lg bg-emerald-500 px-4 py-3 text-sm font-semibold text-white hover:bg-emerald-600">
              {item.is_free ? <Download size={16} /> : <ShoppingCart size={16} />}
              {item.is_free ? 'Download EA' : 'Buy EA'}
            </button>
            {item.tracking_url && <a href={item.tracking_url} target="_blank" rel="noreferrer" className="mt-3 w-full flex items-center justify-center gap-2 rounded-lg bg-[#0d1117] border border-white/10 px-4 py-3 text-sm font-semibold text-gray-200 hover:text-white"><BarChart3 size={16} /> View Tracking Link</a>}
            <p className="text-xs text-gray-600 mt-4">Downloads: {item.download_count || 0}</p>
          </div>

          {!!tags.length && <div className="rounded-xl border border-white/10 bg-white/[0.035] p-5"><p className="text-sm text-gray-300 font-semibold mb-3">Tags</p><div className="flex flex-wrap gap-2">{tags.map((tag) => <Badge key={tag} icon={Tag} text={tag} />)}</div></div>}
        </aside>
      </section>
    </Shell>
  );
}

function Shell({ children }) {
  return <div className="min-h-screen bg-[#070912] text-gray-100"><nav className="fixed top-0 w-full z-50 bg-[#070912]/90 backdrop-blur-xl border-b border-white/10"><div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between"><Link to="/" className="flex items-center gap-2.5"><div className="w-9 h-9 bg-gradient-to-br from-emerald-400 via-teal-400 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/25"><BarChart3 size={18} className="text-white" /></div><span className="text-lg font-bold text-white font-display">TrackMyFxEA</span></Link><Link to="/ea-store" className="text-sm text-emerald-300">EA Store</Link></div></nav><main className="max-w-7xl mx-auto pt-24 pb-16 px-4 md:px-6">{children}</main></div>;
}
function Badge({ icon: Icon, text }) {
  return <span className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-[#0d1117] px-2 py-1 text-[11px] text-gray-300"><Icon size={11} />{text}</span>;
}




